import { getApi } from "../Services/apiService";

/**
 * Which day's "Birthday Today" popup the reader closed on the dashboard, as
 * "YYYY-MM-DD|<family tree id>". It only holds for the session: every login
 * clears it (see clearBirthdaysTodayDismissed), so the popup opens again after
 * each sign-in, however many times a day.
 */
export const BIRTHDAYS_TODAY_DISMISSED_KEY = "famconnect:birthdays-today-dismissed";

/** Called on every successful sign-in so the Birthday Today popup shows again. */
export function clearBirthdaysTodayDismissed() {
  try {
    localStorage.removeItem(BIRTHDAYS_TODAY_DISMISSED_KEY);
  } catch (_) {
    /* private mode — nothing stored to clear */
  }
}

/**
 * The "Birth by Day" report — GET /report-management/birth-by-day
 * (rpt_ByBirthDay).
 *
 * The endpoint does the work: every family member with a date of birth —
 * descendants and in-laws (the Swagger doc still says descendants only) — grouped
 * by the day of the month they were born on (1–31), whatever the month or
 * year. All 31 days come back, empty ones included; each member already sorted
 * by first name. The deceased are in unless `living_only=true`.
 *
 * Sample payload:
 *   { title: "Birth by Day", subtitle: "Descendants Only", family_tree_id,
 *     total_members: 3,
 *     days: [ { day: 5, total_count: 1,
 *       members: [ { id, name: "Edward Jackson", first_name, middle_name,
 *         last_name, suffix, special_name, dob: "-2148249600000",
 *         birth_day: 5, birth_month: 12, birth_year: 1901,
 *         color_name: "Black", hex_code: "#080f1f", generation_level: 1,
 *         present_or_deceased: 2, date_of_death: "927676800000",
 *         head_first_name: null, head_last_name, head_suffix,
 *         head_special_name } ] } ] }
 */

const toNumberOrNull = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const EMPTY_REPORT = { totalCount: 0, days: [] };

/**
 * The report: one entry per day that has anyone born on it, 1 through 31,
 * each { day, members }.
 *
 * @param {string} familyTreeId
 */
export async function fetchBirthByDay(familyTreeId) {
  if (!familyTreeId) return EMPTY_REPORT;

  const response = await getApi(
    `/report-management/birth-by-day?family_tree_id=${encodeURIComponent(familyTreeId)}`
  );

  if (response?.statusCode !== 200 || !response?.data) return EMPTY_REPORT;

  const rawDays = Array.isArray(response.data.days) ? response.data.days : [];

  const days = rawDays
    .map((rawDay) => ({
      day: toNumberOrNull(rawDay?.day),
      members: Array.isArray(rawDay?.members) ? rawDay.members : [],
    }))
    .filter((entry) => entry.day != null && entry.members.length > 0)
    .sort((a, b) => a.day - b.day);

  return {
    totalCount:
      toNumberOrNull(response.data.total_members) ??
      days.reduce((sum, entry) => sum + entry.members.length, 0),
    days,
  };
}

const recordId = (record) => {
  const id = record?.id ?? record?.member_id;
  return id == null || id === "" ? "" : String(id);
};

/**
 * The report's days exactly as the endpoint lists them — nobody is added from
 * the roster. A row is only filled out from its roster record (photos,
 * multiple births, in-law status when the row carries no flag); the report's
 * own fields stay on top, so the in-law tabs and "*" pick its in-laws up.
 *
 * @param {Array<{day: number, members: Array}>} reportDays — fetchBirthByDay().days
 * @param {Array} rosterMembers — every member off dashboard-statistics-with-details
 * @returns {Array<{day: number, details: Array}>} days 1–31 with anyone on them
 */
export function buildBirthByDayGroups(reportDays, rosterMembers = []) {
  const rosterById = new Map();
  (Array.isArray(rosterMembers) ? rosterMembers : []).forEach((member) => {
    const id = recordId(member);
    if (id) rosterById.set(id, member);
  });

  return (reportDays || []).map((entry) => ({
    day: entry.day,
    details: entry.members.map((member) => {
      const roster = rosterById.get(recordId(member));
      const flag = member?.is_in_laws ?? member?.is_in_law ?? member?.is_spouse;
      const row = roster
        ? {
            ...roster,
            ...member,
            present_or_deceased: member.present_or_deceased ?? roster.present_or_deceased,
          }
        : { ...member };
      // In-law status is the report's own flag when it sends one, else the
      // roster record's — the row itself always comes from the report.
      row.is_in_laws =
        flag == null ? roster?.is_in_laws ?? false : flag === true || flag === "true" || flag === 1;
      return row;
    }),
  }));
}
